/**
 * GreedySports — FootballEventPanel 本地假数据（对齐 football_cup_status / 组件 types）
 * **不**作为 Redux store 默认初始值；需在 reducers / Story 等场景显式引用下列 export。
 * 假数据形态由 `USE_MOCK_FOOTBALL_CUP_POINTS_RACE` 决定
 * - false：预选赛（phase 1）进行中；**默认未选队**（`user.team_id === 0`）
 * - true：积分赛（phase 2）进行中；**默认未选队**；`phase_list` 与 `current_phase` 对齐
 * 共 **10 支球队**（`teams` / `rank[]` 仍有数据）；`phase / wheel.countdown` 同源；荣誉阶段 `winners` 与榜前三 team_id 一致。
 */

/**
 * 为 true 时本地模拟 **积分赛进行中**（与 `buildFootballCupMockPhaseList` + `footballEventPanelMockProps.currentPhase` 一致）。
 * 为 false 时仍为 **预选赛**：`footballMockUserQualifying` 默认未选队；若需已选队可把 `team_id` 改为非 0（如与 MOCK_RANK 榜首 3 对齐）。
 */
export const USE_MOCK_FOOTBALL_CUP_POINTS_RACE = false

const placeholder = 'https://img01.mehiya.com/img/png/id/500737086423'
/** 与 `FootballPointsRankingList/images/img-icon-reward-crown.png` 一致，供 rank[].prizes 假数据穿插使用 */
// eslint-disable-next-line global-require, import/no-dynamic-require
const _prizeIconCrownMod = require('./components/FootAct/FootballPointsRankingList/images/img-icon-reward-crown.png')

const prizeIconCrown =
  typeof _prizeIconCrownMod === 'string'
    ? _prizeIconCrownMod
    : _prizeIconCrownMod?.default ?? _prizeIconCrownMod
/** 本地用户头像：`user.member.avatar`、`winners[].top_contributor.avatar` 等 */
const avatar =
  'http://me-media-gateway.test03.youyisia.com/accountv2/view/id/1111565053/sz/src'
/** 与 interface FootballCupPhase 数值一致（仅 mockdata 内使用） */
const CUP_PHASE = {
  NotStarted: 0,
  Qualifying: 1,
  PointsRace: 2,
  HonorDisplay: 3,
  Ended: 4,
}

/** `mockFootballCupStatusData.phase`、`mockFootballCupHomepage.wheel` 共用，避免 countdown 割裂 */
const MOCK_ACTIVITY_COUNTDOWN = 3661

/** 秒；用于生成相对「当前时间」的 phase 窗口，避免写死历史时间戳与 current_phase 不一致 */
const MOCK_SEC = 1
const MOCK_HOUR = 3600 * MOCK_SEC
const MOCK_DAY = 24 * MOCK_HOUR

/**
 * - 预选赛模式：`now` 落在 phase1 内。
 * - 积分赛模式：phase1 已结束，`now` 落在 phase2 内；phase3 顺延。
 */
function buildFootballCupMockPhaseList() {
  const now = Math.floor(Date.now() / 1000)
  if (USE_MOCK_FOOTBALL_CUP_POINTS_RACE) {
    const p1Begin = now - 10 * MOCK_DAY
    const p1End = now - 1 * MOCK_DAY
    const p2Begin = p1End
    const p2End = now + 5 * MOCK_DAY
    const p3Begin = p2End
    const p3End = p3Begin + 2 * MOCK_DAY
    return [
      {phase: CUP_PHASE.Qualifying, begin_ts: p1Begin, end_ts: p1End},
      {phase: CUP_PHASE.PointsRace, begin_ts: p2Begin, end_ts: p2End},
      {phase: CUP_PHASE.HonorDisplay, begin_ts: p3Begin, end_ts: p3End},
    ]
  }
  const p1Begin = now - 2 * MOCK_DAY
  const p1End = now + 1 * MOCK_DAY
  const p2Begin = p1End
  const p2End = p2Begin + 7 * MOCK_DAY
  const p3Begin = p2End
  const p3End = p3Begin + 2 * MOCK_DAY
  return [
    {phase: CUP_PHASE.Qualifying, begin_ts: p1Begin, end_ts: p1End},
    {phase: CUP_PHASE.PointsRace, begin_ts: p2Begin, end_ts: p2End},
    {phase: CUP_PHASE.HonorDisplay, begin_ts: p3Begin, end_ts: p3End},
  ]
}

const MOCK_TEAMS = [
  {team_id: 1, name: 'Team Alpha', icon: placeholder},
  {team_id: 2, name: 'Team Beta', icon: placeholder},
  {team_id: 3, name: 'Team Gamma', icon: placeholder},
  {team_id: 4, name: 'Team Delta', icon: placeholder},
  {team_id: 5, name: 'Team Epsilon', icon: placeholder},
  {team_id: 6, name: 'Team Zeta', icon: placeholder},
  {team_id: 7, name: 'Team Eta', icon: placeholder},
  {team_id: 8, name: 'Team Theta', icon: placeholder},
  {team_id: 9, name: 'Team Iota', icon: placeholder},
  {team_id: 10, name: 'Team Kappa', icon: placeholder},
]

/** 预选赛榜 10 条；与 MOCK_TEAMS 一致 */
const MOCK_RANK = [
  {
    rank_no: 1,
    team_id: 3,
    team_name: 'Team Gamma',
    team_icon: placeholder,
    points: 100000,
    prizes: [
      {icon: prizeIconCrown, name: 'Avatar frame'},
      {icon: placeholder, name: 'Medal'},
      {icon: prizeIconCrown, name: 'Badge'},
      {icon: placeholder, name: 'Title'},
    ],
    pool_coins: 120000,
    top_need_points: 5000,
  },
  {
    rank_no: 2,
    team_id: 1,
    team_name: 'Team Alpha',
    team_icon: placeholder,
    points: 95000,
    prizes: [
      {icon: placeholder, name: 'Silver badge'},
      {icon: prizeIconCrown, name: 'Coin pack'},
      {icon: placeholder, name: 'Frame'},
      {icon: prizeIconCrown, name: 'Emote'},
    ],
    pool_coins: 80000,
    top_need_points: 5000,
  },
  {
    rank_no: 3,
    team_id: 4,
    team_name: 'Team Delta',
    team_icon: placeholder,
    points: 90000,
    prizes: [
      {icon: prizeIconCrown, name: 'Bronze badge'},
      {icon: placeholder, name: 'Gift box'},
      {icon: prizeIconCrown, name: 'Ribbon'},
      {icon: placeholder, name: 'Sticker'},
    ],
    pool_coins: 60000,
    top_need_points: 5000,
  },
  {
    rank_no: 4,
    team_id: 5,
    team_name: 'Team Epsilon',
    team_icon: placeholder,
    points: 86000,
    prizes: [],
    pool_coins: 50000,
    top_need_points: 5000,
  },
  {
    rank_no: 5,
    team_id: 2,
    team_name: 'Team Beta',
    team_icon: placeholder,
    points: 80000,
    prizes: [],
    pool_coins: 45000,
    top_need_points: 5000,
  },
  {
    rank_no: 6,
    team_id: 6,
    team_name: 'Team Zeta',
    team_icon: placeholder,
    points: 75000,
    prizes: [],
    pool_coins: 40000,
    top_need_points: 5000,
  },
  {
    rank_no: 7,
    team_id: 7,
    team_name: 'Team Eta',
    team_icon: placeholder,
    points: 70000,
    prizes: [],
    pool_coins: 35000,
    top_need_points: 5000,
  },
  {
    rank_no: 8,
    team_id: 8,
    team_name: 'Team Theta',
    team_icon: placeholder,
    points: 65000,
    prizes: [],
    pool_coins: 30000,
    top_need_points: 5000,
  },
  {
    rank_no: 9,
    team_id: 9,
    team_name: 'Team Iota',
    team_icon: placeholder,
    points: 60000,
    prizes: [],
    pool_coins: 25000,
    top_need_points: 5000,
  },
  {
    rank_no: 10,
    team_id: 10,
    team_name: 'Team Kappa',
    team_icon: placeholder,
    points: 55000,
    prizes: [],
    pool_coins: 20000,
    top_need_points: 5000,
  },
]

/** 预选赛：未选队（`team_id` 0 时 UI 走未入队 / 选队引导） */
const footballMockUserQualifying = {
  team_id: 0,
  team_rank_no: 0,
  team_size: 0,
  is_eliminated: false,
  wasted_coins: 1200,
  member: {
    id: 100001,
    name: 'MockUser',
    zy_id: 'zy001',
    avatar,
    stealth: false,
  },
  my_rank_no: 0,
  my_points: 0,
}

/** 积分赛：未选队（与预选赛一致，便于测选队前流程） */
const footballMockUserPointsRace = {
  team_id: 0,
  team_rank_no: 0,
  team_size: 0,
  is_eliminated: false,
  wasted_coins: 0,
  member: {
    id: 100001,
    name: 'MockUser',
    zy_id: 'zy001',
    avatar,
    stealth: false,
  },
  my_rank_no: 0,
  my_points: 0,
}

/** @type {import('./components/FootAct/FootballEventPanel/types').FootballEventPanelProps} */
export const footballEventPanelMockProps = {
  currentPhase: USE_MOCK_FOOTBALL_CUP_POINTS_RACE
    ? CUP_PHASE.PointsRace
    : CUP_PHASE.Qualifying,
  countdown: MOCK_ACTIVITY_COUNTDOWN,
  teams: MOCK_TEAMS,
  user: USE_MOCK_FOOTBALL_CUP_POINTS_RACE
    ? footballMockUserPointsRace
    : footballMockUserQualifying,
  pool: {
    jackpot_pool_coins: 500000,
    top_team_pool_coins: 300000,
  },
  /** `currentPhase === 3` 时与 MOCK_RANK 前三名 team_id（3,1,4）一致 */
  winners: [
    {
      rank_no: 1,
      team_id: 3,
      top_contributor: {
        id: 200001,
        name: 'GammaMVP',
        zy_id: 'zy002',
        avatar,
      },
    },
    {
      rank_no: 2,
      team_id: 1,
      top_contributor: {
        id: 200002,
        name: 'AlphaRunnerUp',
        zy_id: 'zy003',
        avatar,
      },
    },
    {
      rank_no: 3,
      team_id: 4,
      top_contributor: {
        id: 200003,
        name: 'DeltaThird',
        zy_id: 'zy004',
        avatar,
      },
    },
  ],
}

/**
 * 与 `footballEventPanelMockProps` 同步的可选假数据（非 store 默认）。
 * @type {import('./reducers').FootballCupStatusData}
 */
export const mockFootballCupStatusData = {
  err_msg: '',
  phase: {
    phase_list: buildFootballCupMockPhaseList(),
    current_phase: footballEventPanelMockProps.currentPhase,
    countdown: footballEventPanelMockProps.countdown,
  },
  teams: footballEventPanelMockProps.teams,
  user: footballEventPanelMockProps.user,
  pool: footballEventPanelMockProps.pool,
  winners:
    footballEventPanelMockProps.currentPhase === CUP_PHASE.HonorDisplay
      ? footballEventPanelMockProps.winners
      : [],
}

/**
 * 首页可选假数据：`phase` / `teams` / `user` 与 mockFootballCupStatusData 对齐（非 store 默认）。
 * @type {import('./reducers').FootballCupHomepageData}
 */
export const mockFootballCupHomepageData = {
  err_msg: '',
  phase: mockFootballCupStatusData.phase,
  teams: mockFootballCupStatusData.teams,
  user: mockFootballCupStatusData.user,
  wheel: {
    countdown: MOCK_ACTIVITY_COUNTDOWN,
    mini_jackpot_pool: 10000,
    minor_jackpot_pool: 20000,
    major_jackpot_pool: 50000,
  },
  rank: MOCK_RANK,
  is_share_done: true,
  push_select: false,
  min_settle_points: 5000,
}

/**
 * 任务接口可选假数据：分享 / 邀请 / 拉新各一条（非 store 默认）。
 * @type {import('./reducers').FootballCupTaskInfoData}
 */
export const mockFootballCupTaskInfoData = {
  err_msg: '',
  list: [
    {
      task_type: 1,
      daily_limit: 3,
      finished: 0,
      points_reward: 50,
      ticket_reward: 0,
      first_extra_ticket: 0,
      first_rewarded: false,
    },
    {
      task_type: 2,
      daily_limit: 5,
      finished: 2,
      points_reward: 100,
      ticket_reward: 1,
      first_extra_ticket: 2,
      first_rewarded: false,
    },
    {
      task_type: 3,
      daily_limit: 10,
      finished: 0,
      points_reward: 200,
      ticket_reward: 2,
      first_extra_ticket: 3,
      first_rewarded: false,
    },
  ],
}

/**
 * 为 true 时 reducer `football_cup_team_rank` 直接返回假数据、不发 HTTP 请求。
 * 接入真实接口后改为 false。
 */
export const USE_MOCK_FOOTBALL_CUP_TEAM_RANK = false

/**
 * 为 true 时 mock 的 `football_cup_team_rank` 返回 **list: []**（队内 Top3 展开为空榜文案）。
 * 测正常假数据时改为 false。
 */
export const USE_MOCK_FOOTBALL_CUP_TEAM_RANK_EMPTY_LIST = true

/**
 * POST /live/game/football_cup_team_rank 本地假数据（队内 Top 成员 list）。
 * @param {number} teamId
 * @returns {import('./reducers').FootballCupTeamRankResult}
 */
export function getMockFootballCupTeamRankResult(teamId) {
  if (USE_MOCK_FOOTBALL_CUP_TEAM_RANK_EMPTY_LIST) {
    return {
      err_msg: '',
      list: [],
    }
  }
  const t = Number(teamId) || 0
  const seed = t * 97
  return {
    err_msg: '',
    list: [
      {
        rank_no: 1,
        points: 12800 + seed,
        member: {
          id: 300000 + t,
          name: `MVP_T${t}`,
          zy_id: `zy_mvp_${t}`,
          avatar,
          stealth: false,
        },
      },
      {
        rank_no: 2,
        points: 10100 + seed,
        member: {
          id: 300100 + t,
          name: `Runner_T${t}`,
          zy_id: `zy_run_${t}`,
          avatar,
          stealth: false,
        },
      },
      {
        rank_no: 3,
        points: 9650 + seed,
        member: {
          id: 300200 + t,
          name: `Third_T${t}`,
          zy_id: `zy_3_${t}`,
          avatar,
          stealth: false,
        },
      },
      {
        rank_no: 4,
        points: 7200 + seed,
        member: {
          id: 300300 + t,
          name: `Fourth_T${t}`,
          zy_id: `zy_4_${t}`,
          avatar,
          stealth: false,
        },
      },
      {
        rank_no: 5,
        points: 5100 + seed,
        member: {
          id: 300400 + t,
          name: `Fifth_T${t}`,
          zy_id: `zy_5_${t}`,
          avatar,
          stealth: false,
        },
      },
    ],
  }
}
